import { Drawer, ButtonBase, Box, LinearProgress, Stack, Typography, IconButton, styled, Badge } from '@mui/material';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';

import Iconify from '../../../components/Iconify';
import { openModal } from '../../../redux/slices/authUiSlice';
import Image from '../../../components/Image';
import useLocales from '../../../hooks/useLocales';
import { useSelector, dispatch } from '../../../redux/store';
import useAuth from '../../../hooks/useAuth';
import { openConfirm } from '../../../redux/slices/confirm';
import useResponsive from '../../../hooks/useResponsive';
import Label from '../../../components/Label';
import getPercentage from '../../../utils/getPercentage';
import { fCurrency } from '../../../utils/formatNumber';

// reusable item
function MenuItem({ item }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      px={1}
      py={1.5}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: '#222' },
      }}
    >
      <Iconify icon={item.icon} width={20} />
      <Typography sx={{ color: '#ADACB1' }} fontSize={'13px'}>
        {item.label}
      </Typography>
    </Box>
  );
}

// reusable section
function MenuSection({ items }) {
  return (
    <Box sx={{ bgcolor: '#1a1a1a' }}>
      {items.map((item) => (
        <MenuItem key={item.label} item={item} />
      ))}
    </Box>
  );
}

const TrxButton = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  cursor: 'pointer',
  justifyContent: 'center',
  alignItems: 'center',
}));

const TrxImageBox = styled(Box)(({ theme }) => ({
  background: '#fff',
  padding: 9,
  borderRadius: '.375rem',
  width: 'fit-content',
}));

const MenuText = styled(Typography)(({ theme }) => ({
  color: '#242424',
  fontSize: 12,
}));

const MenuBox = styled(Stack)(({ theme }) => ({
  cursor: 'pointer',
  padding: 13,
  borderBottom: '1px solid #ebebeb',
  alignItems: 'center',
}));

export default function AccountDrawer({ open, onClose }) {
  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const { memberBalance, withdrawRequirement } = useSelector((x) => ({ ...x.lookup }));

  const { isAuthenticated, userProfile, logout } = useAuth();

  const navigate = useNavigate();

  const isDesktop = useResponsive('up', 'lg');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isDesktop ? '400px' : '100%',
          height: isDesktop ? '100%' : 'calc(100% - 50px)',
          bgcolor: '#f7f7f7',
          color: '#000',
        },
      }}
      sx={{
        height: 'calc(100% - 50px)',
        '& > div:nth-child(1)': {
          height: 'calc(100% - 50px)',
        },
      }}
    >
      {isAuthenticated ? (
        <Stack
          spacing={'1rem'}
          sx={{
            p: '1.5rem',
            background: 'linear-gradient(180deg,#4b734e,#203127)',
            borderBottomLeftRadius: '1.5rem',
            borderBottomRightRadius: '1.5rem',
          }}
        >
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{ fontSize: '.875rem', color: '#fff', flexGrow: 1, textTransform: 'uppercase', fontWeight: 500 }}
              >
                Hi, {userProfile?.username}
              </Typography>

              <Label
                sx={{
                  bgcolor: userProfile?.is_kyc_verified ? '#d4edda' : '#f8d7da',
                  color: userProfile?.is_kyc_verified ? '#2e7d32' : '#c62828',
                  px: 0.5,
                  py: 0.15,
                  fontSize: '0.6rem',
                }}
              >
                <Box component={'span'} sx={{ fontWeight: 900, textTransform: 'uppercase' }}>
                  {userProfile?.is_kyc_verified ? translate('verified') : translate('unverified')}
                </Box>
              </Label>
            </Box>
            <ButtonBase
              onClick={() => {
                dispatch(
                  openConfirm({
                    title: translate('logout_confirmation'),
                    subTitle: translate('are_you_sure_to_logout'),
                    onConfirm: handleLogout,
                  })
                );
                onClose();
              }}
              sx={{ fontSize: '.875rem', color: '#f1c16d' }}
            >
              {translate('logout')}
            </ButtonBase>
          </Stack>

          <Stack direction="row">
            <Typography
              sx={{ fontSize: '.875rem', color: '#fff', flexGrow: 1, textTransform: 'uppercase', fontWeight: 500 }}
            >
              {`${translate('vip_level')}: ${userProfile?.MemberGroup?.name}`}
            </Typography>
          </Stack>

          <Stack>
            <Typography sx={{ color: '#fff', fontSize: '.75rem' }}>{translate('credit')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 'fit-content' }}>
              <Typography
                sx={{ fontSize: '1.125rem', color: '#fff', flexGrow: 1, textTransform: 'uppercase', fontWeight: 700 }}
              >
                {memberBalance?.currencyCode}
              </Typography>
              <Typography
                sx={{ fontSize: '1.125rem', color: '#fff', flexGrow: 1, textTransform: 'uppercase', fontWeight: 700 }}
              >
                {memberBalance?.displayBalance}
              </Typography>
              <Iconify icon="mynaui:refresh-solid" sx={{ color: '#fff', width: 14, height: 14 }} />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ p: '1rem', display: 'flex' }}>
          <ButtonBase
            onClick={() => {
              onClose();
              dispatch(
                openModal({
                  view: 'login',
                })
              );
            }}
            sx={{
              background: 'linear-gradient(180deg,#ffe08d,#e09944)',
              p: '.5em .875em',
              color: '#000',
              fontSize: '16px',
              m: 'auto',
            }}
          >
            {translate('login_now')}
          </ButtonBase>
        </Box>
      )}
      <Stack direction="row" sx={{ mt: '1rem' }}>
        <TrxButton
          onClick={() => {
            navigate('/user/wallet?tab=deposit');
            onClose();
          }}
          spacing={'.5rem'}
        >
          <TrxImageBox>
            <Image src={require('../../../assets/menu/deposit.png')} sx={{ width: '25px', height: '25px' }} />
          </TrxImageBox>
          <MenuText>{translate('deposit')}</MenuText>
        </TrxButton>

        <TrxButton
          onClick={() => {
            navigate('/user/wallet?tab=withdraw');
            onClose();
          }}
          spacing={'.5rem'}
        >
          <TrxImageBox>
            <Image src={require('../../../assets/menu/withdraw.png')} sx={{ width: '25px', height: '25px' }} />
          </TrxImageBox>
          <MenuText>{translate('withdraw')}</MenuText>
        </TrxButton>

        <TrxButton
          onClick={() => {
            navigate('/history');
            onClose();
          }}
          spacing={'.5rem'}
        >
          <TrxImageBox>
            <Image src={require('../../../assets/menu/history.png')} sx={{ width: '25px', height: '25px' }} />
          </TrxImageBox>
          <MenuText>{translate('history')}</MenuText>
        </TrxButton>
      </Stack>

      <Stack sx={{ background: '#fff', m: '1rem' }}>
        {isAuthenticated && !userProfile?.is_kyc_verified ? (
          <MenuBox
            direction={'row'}
            spacing={'.875rem'}
            onClick={() => {
              navigate('/user/kyc');
              onClose();
            }}
          >
            <Iconify icon={'solar:user-id-outline'} sx={{ width: '25px', height: '25px' }} />
            <MenuText>{translate('kyc_verification')}</MenuText>

            <Box sx={{ flex: 1 }} />

            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#ff4d4f',
              }}
            />
          </MenuBox>
        ) : (
          ''
        )}

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/vip');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/vip.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('vip')}</MenuText>
        </MenuBox>

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/user/account');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/security.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('security_center')}</MenuText>
        </MenuBox>

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/referral');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/referral.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('referral')}</MenuText>
        </MenuBox>

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/promotion/tab');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/promotion.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('promotion')}</MenuText>
        </MenuBox>

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/history');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/history.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('history')}</MenuText>
        </MenuBox>

        <MenuBox
          direction={'row'}
          spacing={'.875rem'}
          onClick={() => {
            navigate('/user/banks');
            onClose();
          }}
        >
          <Image src={require('../../../assets/menu/bank.png')} sx={{ width: '25px', height: '25px' }} />
          <MenuText>{translate('bank_details')}</MenuText>
        </MenuBox>
      </Stack>

      {(withdrawRequirement?.calculation_type === 'turnover' || withdrawRequirement?.calculation_type === 'winover') &&
        withdrawRequirement?.requirement !== 0 && (
          <>
            <Box
              sx={{ background: 'linear-gradient(180deg, #3f6b3f 0%, #1e3d2b 100%)', borderRadius: 1, p: { xs: 1 }, m: 1 }}
              mb={"5rem"}
            >
              <Typography sx={{ color: '#fff' }}>
                {`${
                  withdrawRequirement?.calculation_type === 'turnover' ? translate('turnover') : translate('balance')
                } ${translate('requirement')}`}
              </Typography>
              <Stack direction="column" sx={{ p: 1, border: '1px solid #febc65', borderRadius: 1, width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={getPercentage(withdrawRequirement?.current, withdrawRequirement?.requirement)}
                  color="warning"
                  sx={{ height: '20px', borderRadius: 2 }}
                />

                <Typography sx={{ color: '#fff' }}>
                  {fCurrency(withdrawRequirement?.current)} / {fCurrency(withdrawRequirement?.requirement)}
                </Typography>
              </Stack>
            </Box>
          </>
        )}
    </Drawer>
  );
}
